/**
 * RACI Matrix Template Form
 * Full RACI (Responsible, Accountable, Consulted, Informed) matrix for project tasks
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const RACIMatrixForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '',
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    stakeholders: [''],
    tasks: [{
      task: '',
      category: '',
      responsible: '',
      accountable: '',
      consulted: '',
      informed: '',
      notes: ''
    }],
    followups: []
  });

  const getSampleData = () => ({
    project_name: 'CRM Platform Implementation',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'Project Manager',
    stakeholders: ['Product Manager', 'Tech Lead', 'DevOps Engineer', 'QA Lead', 'Business Analyst', 'Stakeholders'],
    tasks: [
      { task: 'Requirements Gathering', category: 'Planning', responsible: 'Business Analyst', accountable: 'Product Manager', consulted: 'Tech Lead, Stakeholders', informed: 'DevOps Engineer, QA Lead', notes: 'Phase 1 deliverable' },
      { task: 'System Architecture Design', category: 'Design', responsible: 'Tech Lead', accountable: 'CTO', consulted: 'DevOps Engineer, Business Analyst', informed: 'Product Manager, QA Lead', notes: 'Requires security review' },
      { task: 'Database Schema Design', category: 'Design', responsible: 'Tech Lead', accountable: 'Tech Lead', consulted: 'Business Analyst', informed: 'DevOps Engineer', notes: '' },
      { task: 'Frontend Development', category: 'Development', responsible: 'Frontend Team', accountable: 'Tech Lead', consulted: 'Product Manager, UX Designer', informed: 'QA Lead', notes: 'Sprint 2-4' },
      { task: 'Backend API Development', category: 'Development', responsible: 'Backend Team', accountable: 'Tech Lead', consulted: 'Business Analyst', informed: 'Frontend Team, QA Lead', notes: 'Sprint 2-5' },
      { task: 'QA Testing', category: 'Testing', responsible: 'QA Lead', accountable: 'QA Lead', consulted: 'Tech Lead, Business Analyst', informed: 'Product Manager, DevOps Engineer', notes: 'Includes UAT' },
      { task: 'Infrastructure Setup', category: 'Deployment', responsible: 'DevOps Engineer', accountable: 'Tech Lead', consulted: 'Tech Lead', informed: 'QA Lead, Product Manager', notes: 'AWS environment' },
      { task: 'Production Deployment', category: 'Deployment', responsible: 'DevOps Engineer', accountable: 'CTO', consulted: 'Tech Lead, QA Lead', informed: 'All Stakeholders', notes: 'Go-live checklist required' },
      { task: 'User Training', category: 'Training', responsible: 'Business Analyst', accountable: 'Product Manager', consulted: 'Tech Lead', informed: 'Stakeholders', notes: 'Documentation needed' },
      { task: 'Post-launch Support', category: 'Support', responsible: 'Support Team', accountable: 'Tech Lead', consulted: 'DevOps Engineer, QA Lead', informed: 'Product Manager', notes: '30-day hypercare' }
    ],
    followups: [
      { item: 'Schedule architecture review meeting', owner: 'Tech Lead', due_date: '2025-02-01', priority: 'High', status: 'Pending' },
      { item: 'Define UAT test scenarios', owner: 'QA Lead', due_date: '2025-02-15', priority: 'Medium', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "RACI Matrix Draft";
      const payload = {
        name: draftName,
        template_type: "RACI Matrix",
        template_id: "raci-matrix",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "r-a-c-i-matrix", "R A C I Matrix", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("r-a-c-i-matrix", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/raci-matrix`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `RACI_Matrix_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "RACI Matrix",
        formData,
        settings: activeSettings || {},
        templateType: "RACI Matrix",
        templateId: "raci-matrix",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addTask = () => {
    setFormData({
      ...formData,
      tasks: [...formData.tasks, { task: '', category: '', responsible: '', accountable: '', consulted: '', informed: '', notes: '' }]
    });
  };

  const updateTask = (index, field, value) => {
    const tasks = [...formData.tasks];
    tasks[index] = { ...tasks[index], [field]: value };
    setFormData({ ...formData, tasks });
  };

  const removeTask = (index) => {
    setFormData({ ...formData, tasks: formData.tasks.filter((_, i) => i !== index) });
  };

  const addStakeholder = () => {
    setFormData({ ...formData, stakeholders: [...formData.stakeholders, ''] });
  };

  const updateStakeholder = (index, value) => {
    const stakeholders = [...formData.stakeholders];
    stakeholders[index] = value;
    setFormData({ ...formData, stakeholders });
  };

  const removeStakeholder = (index) => {
    setFormData({ ...formData, stakeholders: formData.stakeholders.filter((_, i) => i !== index) });
  };

  const categories = ['Planning', 'Design', 'Development', 'Testing', 'Deployment', 'Training', 'Support', 'Governance', 'Other'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">RACI Matrix</h1>
                <p className="text-sm text-gray-500">Responsibility assignment matrix for project tasks</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample Data
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Project Information</CardTitle>
            <p className="text-sm text-gray-500">R = Responsible (does the work) | A = Accountable (decision maker) | C = Consulted (provides input) | I = Informed (kept updated)</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input 
                  value={formData.project_name} 
                  onChange={(e) => setFormData({...formData, project_name: e.target.value})} 
                  placeholder="Project name" 
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData({...formData, date: e.target.value})} 
                />
              </div>
              <div>
                <Label>Prepared By</Label>
                <Input 
                  value={formData.prepared_by} 
                  onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} 
                  placeholder="Your name/role" 
                />
              </div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Stakeholders / Roles</Label>
              <p className="text-xs text-gray-500 mb-2">List all people/roles involved in this project</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {formData.stakeholders.map((stakeholder, idx) => (
                  <div key={idx} className="flex gap-1">
                    <Input 
                      value={stakeholder} 
                      onChange={(e) => updateStakeholder(idx, e.target.value)} 
                      placeholder="Role/Person"
                      className="text-sm"
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeStakeholder(idx)} className="shrink-0">
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addStakeholder} className="mt-2">
                <Plus className="h-4 w-4 mr-1" /> Add Stakeholder
              </Button>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Tasks / Activities</Label>
              <p className="text-xs text-gray-500 mb-3">Define tasks and assign RACI roles</p>
              
              <div className="space-y-3">
                {formData.tasks.map((task, idx) => (
                  <div key={idx} className="grid grid-cols-1 lg:grid-cols-8 gap-2 p-3 bg-gray-50 rounded-lg border">
                    <div className="lg:col-span-2">
                      <Label className="text-xs">Task/Activity</Label>
                      <Input 
                        value={task.task} 
                        onChange={(e) => updateTask(idx, 'task', e.target.value)} 
                        placeholder="Task description"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Category</Label>
                      <Select value={task.category} onValueChange={(v) => updateTask(idx, 'category', v)}>
                        <SelectTrigger className="text-sm h-9">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-blue-600 font-medium">Responsible (R)</Label>
                      <Input 
                        value={task.responsible} 
                        onChange={(e) => updateTask(idx, 'responsible', e.target.value)} 
                        placeholder="Who does it"
                        className="text-sm border-blue-200"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-orange-600 font-medium">Accountable (A)</Label>
                      <Input 
                        value={task.accountable} 
                        onChange={(e) => updateTask(idx, 'accountable', e.target.value)} 
                        placeholder="Decision maker"
                        className="text-sm border-orange-200"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-yellow-600 font-medium">Consulted (C)</Label>
                      <Input 
                        value={task.consulted} 
                        onChange={(e) => updateTask(idx, 'consulted', e.target.value)} 
                        placeholder="Provides input"
                        className="text-sm border-yellow-200"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-green-600 font-medium">Informed (I)</Label>
                      <Input 
                        value={task.informed} 
                        onChange={(e) => updateTask(idx, 'informed', e.target.value)} 
                        placeholder="Kept updated"
                        className="text-sm border-green-200"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button variant="ghost" size="icon" onClick={() => removeTask(idx)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addTask} className="mt-3">
                <Plus className="h-4 w-4 mr-1" /> Add Task
              </Button>
            </div>

            <Separator />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default RACIMatrixForm;
